import React, { useCallback, useEffect, useState } from "react";

import { Close } from "@mui/icons-material";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography
} from "@mui/material";
import { ValidationError } from "components/Validation-error/ValidationError";
import { useAccountSelector } from "store/selectors/user-selectors";
import { DateRange } from "store/types";

const selectStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px"
  },
  "& .MuiSelect-select": {
    padding: "8px !important"
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7B92A9"
  }
};

const quarters = ["Q1", "Q2", "Q3", "Q4"];
const currentYear = new Date().getUTCFullYear();
const years = Array.from({ length: 54 }, (_, i) => currentYear - i);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dateRange: DateRange, use_accounting_date?: boolean) => void;
  defaultUseAccountingDate: boolean;
  /** Hides the "From inception" checkbox and the Effective/Accounting date switch. */
  hideExtraOptions?: boolean;
}

export const QuarterRangeDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  defaultUseAccountingDate,
  hideExtraOptions
}) => {
  const isRangeSwitchEnabled = useAccountSelector()?.allow_use_date_range_switch || false;
  const [yearFrom, setYearFrom] = useState<number | null>(null);
  const [yearTo, setYearTo] = useState<number | null>(null);
  const [quarterFrom, setQuarterFrom] = useState<string>("Q1");
  const [quarterTo, setQuarterTo] = useState<string>("Q1");
  const [fromInception, setFromInception] = useState(false);
  const [useAccountingDate, setUseAccountingDate] = useState<boolean>(!!defaultUseAccountingDate);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    const getQuarterDate = (year: number, quarter: string, isEnd?: boolean) => {
      const month = quarters.indexOf(quarter) * 3 + (isEnd ? 2 : 0);
      const date = isEnd ? new Date(Date.UTC(year, month + 1, 0)) : new Date(Date.UTC(year, month, 1));
      return date.toISOString().split("T")[0];
    };

    const dateRange: DateRange & { use_accounting_date?: boolean } = {};

    if (!fromInception && yearFrom && quarterFrom) {
      dateRange.from = getQuarterDate(yearFrom, quarterFrom, false);
    }

    if (yearTo && quarterTo) {
      dateRange.to = getQuarterDate(yearTo, quarterTo, true);
    }

    onSubmit(dateRange, useAccountingDate);
    onClose();
  }, [yearFrom, yearTo, quarterFrom, quarterTo, fromInception, useAccountingDate]);

  useEffect(() => {
    if (yearFrom && yearTo && yearFrom === yearTo && quarterFrom > quarterTo && !fromInception) {
      setValidationError("Date from must be less than date to");
    } else {
      setValidationError(null);
    }
  }, [yearFrom, yearTo, quarterFrom, quarterTo, fromInception]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <Stack sx={{ padding: "32px", maxWidth: "455px" }}>
        <Stack
          sx={{ borderBottom: "1px solid #dae0e2" }}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography p={0} color="#071F35" fontSize="18px">
            Quarter range
          </Typography>
          <IconButton sx={{ padding: "3px" }} onClick={onClose}>
            <Close sx={{ fontSize: "20px", color: "#5d7782" }} />
          </IconButton>
        </Stack>
        <DialogContent sx={{ padding: "10px 0px" }}>
          <Typography fontSize="14px" fontWeight="400" color="#496682" mb="10px">
            From the start of the first quarter to the end of the last quarter you select.
          </Typography>
          <Stack gap="10px">
            <Stack gap="10px" flexDirection="row" alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl sx={{ width: 100 }}>
                  <InputLabel>Year From</InputLabel>
                  <Select
                    value={yearFrom}
                    label="Year From"
                    onChange={(e) => setYearFrom(e.target.value as number)}
                    disabled={fromInception}
                    sx={selectStyles}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: "370px"
                        }
                      }
                    }}
                  >
                    {years.map((year) => (
                      <MenuItem disabled={Boolean(yearTo && year > yearTo)} key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 75 }}>
                  <InputLabel>Quarter</InputLabel>
                  <Select
                    value={quarterFrom}
                    label="Quarter"
                    onChange={(e) => setQuarterFrom(e.target.value)}
                    disabled={fromInception}
                    sx={selectStyles}
                  >
                    {quarters.map((q) => (
                      <MenuItem key={q} value={q}>
                        {q}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              -
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControl sx={{ width: 100 }}>
                  <InputLabel>Year To</InputLabel>
                  <Select
                    value={yearTo}
                    label="Year To"
                    onChange={(e) => setYearTo(e.target.value as number)}
                    sx={selectStyles}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: "370px"
                        }
                      }
                    }}
                  >
                    {years.map((year) => (
                      <MenuItem disabled={Boolean(yearFrom && year < yearFrom)} key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ width: 75 }}>
                  <InputLabel>Quarter</InputLabel>
                  <Select
                    value={quarterTo}
                    label="Quarter"
                    onChange={(e) => setQuarterTo(e.target.value)}
                    sx={selectStyles}
                  >
                    {quarters.map((q) => (
                      <MenuItem key={q} value={q}>
                        {q}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>

            {!hideExtraOptions && (
              <FormControlLabel
                control={<Checkbox checked={fromInception} onChange={(e) => setFromInception(e.target.checked)} />}
                label="From inception"
              />
            )}

            {validationError && <ValidationError messages={validationError} />}

            {!hideExtraOptions && isRangeSwitchEnabled && (
              <FormControl>
                <RadioGroup row value={useAccountingDate} onChange={(e) => setUseAccountingDate(e.target.value === "true")}>
                  <FormControlLabel value={false} control={<Radio />} label="Effective Date" />
                  <FormControlLabel value={true} control={<Radio />} label="Accounting Date" />
                </RadioGroup>
              </FormControl>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "0" }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={Boolean(validationError)} variant="contained" onClick={handleSubmit} color="primary">
            OK
          </Button>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};
